export const deviceTypes = [
  'aciLeaf',
  'aciSpine',
  'ap',
  'apic',
  'awsAlb',
  'awsCne',
  'awsDxgw',
  'awsEni',
  'awsIgw',
  'awsInstance',
  'awsNatGw',
  'awsNlb',
  'awsSubnet',
  'awsTgw',
  'awsVpc',
  'awsVpcPeering',
  'awsVgw',
  'azureAppGw',
  'azureErc',
  'azureFw',
  'azureGw',
  'azureLb',
  'azureNat',
  'azureInstance',
  'azureNic',
  'azureSubnet',
  'azureVhub',
  'azureVnet',
  'azureVnetPeering',
  'cloudInstance',
  'cloudInternetGw',
  'cloudLoadBalancer',
  'cloudNatGw',
  'cloudRouter',
  'cloudTransitHub',
  'cloudVpnGw',
  'consoleServer',
  'fex',
  'fw',
  'gcpCloudNat',
  'gcpInstance',
  'gcpLb',
  'gcpNic',
  'gcpServiceConnect',
  'gcpRouter',
  'gcpSubnet',
  'gcpVpc',
  'gcpVpcPeering',
  'host',
  'l3switch',
  'lb',
  'networkManagement',
  'nx7000',
  'phone',
  'router',
  'securityManagement',
  'server',
  'switch',
  'unknown',
  'vgw',
  'waas',
  'wlc',
  // NIM-21654 Those icons are implemented and ready to use, but should not appear in the app
  // until they are actually supported by Discovery.
  // 'awsGwlb',
  // 'awsGwVpce',
  // 'awsPrivateLink',
  // 'awsPrivateLinkEndpoint',
] as const;
export const nodeTypes = [
  ...deviceTypes,
  'cloud',
  'hostGroup',
  'phoneGroup',
  'transit',
] as const;

export const deviceTypeSet: Set<string> = new Set(deviceTypes);
export const nodeTypeSet: Set<string> = new Set(nodeTypes);

export type DeviceType = (typeof deviceTypes)[number];
export type NodeType = (typeof nodeTypes)[number];

// Cloud network constructs (VPC/VNet) are stored as graph nodes with one of
// these types - see saveCloudNetworkNodes and CLOUD_NETWORK_NODE_TYPES_SQL in
// postgresGraphDataProvider. Unlike regular devices they are not present in the
// vDevice collection.
export const cloudNetworkNodeTypes = [
  'awsVpc',
  'azureVnet',
  'gcpVpc',
] as const satisfies readonly NodeType[];

// Cloud network interface (vNIC) node types — produced by the PathLookup graph
// convertor, one per vendor.
export const cloudNetworkInterfaceNodeTypes = [
  'awsEni',
  'azureNic',
  'gcpNic',
] as const satisfies readonly NodeType[];

// Cloud subnet node types — produced by the PathLookup graph convertor
//, one per vendor.
export const cloudSubnetNodeTypes = [
  'awsSubnet',
  'azureSubnet',
  'gcpSubnet',
] as const satisfies readonly NodeType[];

// Cloud network peering node types — produced by the PathLookup graph
// convertor, one per vendor. In the topology graph the
// peering is rendered as an edge, not a node.
export const cloudPeeringNodeTypes = [
  'awsVpcPeering',
  'azureVnetPeering',
  'gcpVpcPeering',
] as const satisfies readonly NodeType[];

// Cloud VM instance node types — produced by the PathLookup graph convertor
// when a path terminates at an NCM-discovered VM (NIM-24386), one per vendor.
// `cloudInstance` is the fallback used when the vendor can't be resolved.
export const cloudInstanceNodeTypes = [
  'cloudInstance',
  'awsInstance',
  'azureInstance',
  'gcpInstance',
] as const satisfies readonly NodeType[];

// Cloud NAT gateway node types, one per vendor. `cloudNatGw` is the fallback
// used when the vendor can't be resolved.
export const cloudNatGwNodeTypes = [
  'cloudNatGw',
  'awsNatGw',
  'azureNat',
  'gcpCloudNat',
] as const satisfies readonly NodeType[];

// Cloud internet gateway node types. AWS only — neither Azure nor GCP expose a
// discrete internet-gateway construct. `cloudInternetGw` is the fallback used
// when the vendor can't be resolved.
export const cloudInternetGwNodeTypes = [
  'cloudInternetGw',
  'awsIgw',
] as const satisfies readonly NodeType[];

// All node types that represent a first-class cloud construct in either the
// topology graph (Cloud Network only) or the PathLookup graph (all five).
export const cloudConstructNodeTypes = [
  ...cloudNetworkNodeTypes,
  ...cloudNetworkInterfaceNodeTypes,
  ...cloudSubnetNodeTypes,
  ...cloudPeeringNodeTypes,
  ...cloudInstanceNodeTypes,
  ...cloudNatGwNodeTypes,
  ...cloudInternetGwNodeTypes,
] as const satisfies readonly NodeType[];

export const NODE_LABEL_FONT = 'OpenSans-Regular';
export const NODE_LABEL_FONT_SIZE = 12;
